import type { Locale } from "./dto";

/**
 * F116 (Pavel 2026-08-11: "pro tyhle lidi se potrebujeme zajistit, ze uplne
 * vsechno je perfektne v americke anglictine"). ONE reading of "which of our
 * two languages does this reader want", shared by the browser (the login
 * screen, before any session exists) and the Worker (the /go/ and /k/ landing
 * pages, which render outside the SPA).
 *
 * A second copy is how the two would drift: the login page answered English
 * while the landing page it links to answered Czech.
 */

/**
 * The first tag whose primary subtag we actually speak wins, so
 * ["de","en-US","cs"] answers "en" - English is the closer of the two for a
 * reader who asked for neither.
 *
 * 🔴 F119 (Pavel 2026-08-11: "Kdyz si nejsme jisti jazykem, musi to byt
 * anglicky."): nothing we speak at all answers ENGLISH, not Czech. This is the
 * LAST resort of every language chain in the product, so it is the one place
 * the rule has to hold. Czech is the answer of a KNOWN Czech reader - a client
 * whose market is Czech, an account that stored `cs`, a browser that asked for
 * it (["sk","cs"] is still Czech, because the reader said so). It stopped being
 * the answer of a reader who told us nothing: a Slovak, a German and an
 * American all read English better than Czech, and only one of those three
 * used to get it.
 */
export function preferredLocale(tags: readonly string[]): Locale {
  for (const tag of tags) {
    const primary = tag.trim().toLowerCase().split("-")[0];
    if (primary === "cs" || primary === "en") return primary;
  }
  return "en";
}

/**
 * `Accept-Language: cs-CZ,cs;q=0.9,en;q=0.8` in the reader's own order of
 * preference. The q-value is what states that order, so it is sorted by, not
 * merely parsed away; a tag without one means q=1 (the header's own default).
 * A malformed q is dropped rather than read as 0, or one bad character would
 * silently push a tag to the back of the queue.
 */
export function acceptLanguageTags(header: string | null | undefined): string[] {
  if (!header) return [];
  return header
    .split(",")
    .flatMap((part, index) => {
      const [tag, ...params] = part.split(";").map((piece) => piece.trim());
      if (!tag || tag === "*") return [];
      const quality = params
        .map((param) => /^q=(\d+(?:\.\d+)?)$/i.exec(param))
        .find(Boolean);
      const parsed = quality ? Number(quality[1]) : 1;
      if (!Number.isFinite(parsed) || parsed <= 0) return [];
      // The index keeps the header's own order stable inside one q value.
      return [{ tag, quality: Math.min(parsed, 1), index }];
    })
    .sort((left, right) => right.quality - left.quality || left.index - right.index)
    .map((entry) => entry.tag);
}
